import { useCallback, useEffect, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

/** Openstand van één huurder, zoals ze in de boekhouding staat. */
export interface TenantBalance {
  /** Totaal openstaand bedrag (euro) over alle open facturen. */
  totalDebt: number;
  /** Aantal open facturen. */
  invoiceCount: number;
  /** Hoogste aantal dagen te laat over die facturen. */
  maxDaysOverdue: number;
  /** Het incassodossier waar dit uit komt — om naartoe te linken. */
  incassoTicketId: number;
  /** Fase van dat incassodossier. */
  phase: string;
}

/** Sleutel in het antwoord: `contact:<id>` of `company:<id>`. */
export const tenantBalanceKey = (
  type: 'contact' | 'company',
  id: number | string | null | undefined,
) => (id ? `${type}:${id}` : null);

/**
 * Openstand van een reeks huurders, in **één** call.
 *
 * De cijfers komen van de incassodossiers (`cachedTotalDebt` en co.), die de dagelijkse
 * Odoo-sync bijwerkt — er wordt dus niet per weergave naar de boekhouding gebeld.
 *
 * Bedoeld om overal waar een huurder getoond wordt zijn openstand erbij te zetten
 * (helpdesk, renovatietickets, …) zonder per plek een eigen query. Ontbreekt een sleutel
 * in het resultaat, dan heeft die huurder geen incassodossier en dus geen openstand.
 *
 * Fouten gaan naar Sentry maar niet naar een toast: de openstand is aanvullende
 * informatie en mag de pagina waarop ze staat niet met een foutmelding overladen.
 */
export function useTenantBalances(
  contactIds: (number | string | null | undefined)[] = [],
  companyIds: (number | string | null | undefined)[] = [],
) {
  const { session } = useSession();
  const [balances, setBalances] = useState<Record<string, TenantBalance>>({});
  const [isLoading, setIsLoading] = useState(false);

  // Gesorteerde, ontdubbelde sleutels: zo verandert de dependency niet bij elke render
  // wanneer de caller een nieuwe array met dezelfde ids doorgeeft.
  const contactKey = Array.from(new Set(contactIds.filter(Boolean).map(Number)))
    .sort((a, b) => a - b)
    .join(',');
  const companyKey = Array.from(new Set(companyIds.filter(Boolean).map(Number)))
    .sort((a, b) => a - b)
    .join(',');

  const refresh = useCallback(async () => {
    if (!session || (!contactKey && !companyKey)) {
      setBalances({});
      return;
    }
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (contactKey) params.set('contactIds', contactKey);
      if (companyKey) params.set('companyIds', companyKey);
      const res = await fetchWithAuth(
        `${API_URL}/rental/incasso-tickets/outstanding-balances?${params}`,
        { method: 'GET' },
        session,
      );
      if (!res.ok) throw new Error(`Openstand ophalen mislukt (${res.status})`);
      setBalances(await res.json());
    } catch (error) {
      Sentry.captureException(error, {
        tags: { module: 'tenant-balance', action: 'useTenantBalances' },
        extra: { contactKey, companyKey },
      });
      setBalances({});
    } finally {
      setIsLoading(false);
    }
  }, [session, contactKey, companyKey]);

  useEffect(() => { refresh(); }, [refresh]);

  return { balances, isLoading, refresh };
}
